import React, { useState, useEffect } from 'react';
import { ArrowRight, BookOpen, Users, GraduationCap, X, PlayCircle, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCaptchaToken, verifyCaptcha } from '../utils/captcha';
import { submitForm } from '../utils/api';
import { useData } from '../context/DataContext';

const AcademyPage = () => {
  const { data, loading } = useData();
  const [activePlaylistId, setActivePlaylistId] = useState('');
  const [showEarlyBird, setShowEarlyBird] = useState(false);
  const [ebForm, setEbForm] = useState({ name: '', email: '', interest: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    let earlyBirdShown = false;
    const timer = setTimeout(() => {
      try {
        earlyBirdShown = sessionStorage.getItem('earlyBirdShown') === '1';
      } catch (e) {}
      
      if (!earlyBirdShown) {
        try {
          sessionStorage.setItem('earlyBirdShown', '1');
        } catch (e) {}
          setShowEarlyBird(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleEbSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('academy_early_bird');
      const captchaResult = await verifyCaptcha(token, 'academy_early_bird');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      await submitForm('academy_early_bird', ebForm);
      toast.success("You're on the list! We'll notify you when the platform launches. 🚀");
      setIsSubmitting(false);
      setShowEarlyBird(false);
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to submit early bird registration. Please try again.');
      setIsSubmitting(false);
    }
  };

  const playlists = data?.academyData || [];

  // Sync active playlist ID if not set
  useEffect(() => {
    if (playlists.length > 0 && !activePlaylistId) {
      setActivePlaylistId(playlists[0].playlistId);
    }
  }, [playlists, activePlaylistId]);

  if (loading || !data || !data.academyData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  const activePlaylist = playlists.find(p => p.playlistId === activePlaylistId) || playlists[0];
  const activeVideos = activePlaylist ? (activePlaylist.videos || []) : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      
      {/* Early Bird Popup Modal */}
      {showEarlyBird && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setShowEarlyBird(false)}
              className="absolute top-4 right-4 p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10"
            >
              <X size={20} />
            </button>
            <div className="p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-2">🎉 Early Bird Offer — Coming Soon!</h2>
              <p className="text-slate-400 mb-6 font-light">Register now and get FREE ₹399 credits on our learning platform at launch.</p>
              <form onSubmit={handleEbSubmit} className="space-y-4">
                <Input required name="name" value={ebForm.name} onChange={(e) => setEbForm(p => ({...p, name: e.target.value}))} placeholder="Name" className="bg-slate-800/50 border-slate-700 text-white" />
                <Input required type="email" name="email" value={ebForm.email} onChange={(e) => setEbForm(p => ({...p, email: e.target.value}))} placeholder="Email" className="bg-slate-800/50 border-slate-700 text-white" />
                <Select onValueChange={(val) => setEbForm(p => ({...p, interest: val}))}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue placeholder="Interest (Optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">
                    <SelectItem value="Fundraising Readiness">Fundraising Readiness</SelectItem>
                    <SelectItem value="Financial Modelling for Founders">Financial Modelling for Founders</SelectItem>
                    <SelectItem value="Investor Storytelling & Pitch">Investor Storytelling & Pitch</SelectItem>
                    <SelectItem value="Strategy & Execution Bootcamps">Strategy & Execution Bootcamps</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-medium">
                  {isSubmitting ? 'Submitting...' : 'Claim My Free Credits'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-32 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">Learning & Development</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Fundraiseo Academy
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              Capacity building programs in fundraising, financial modelling, and strategic 
              thinking for founders and their teams. Learn from experienced practitioners through 
              structured workshops and bootcamps.
            </p>
          </div>
        </div>
      </section>

      {/* Two Pane Layout */}
      <section className="py-12 bg-slate-900/50 flex-1">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-8">
            
            {/* Left Pane - Vertical Topic Navigation */}
            <div className="w-full md:w-1/4">
              <div className="sticky top-28">
                <h3 className="text-xl font-bold text-white mb-6 hidden md:block">Our Programs</h3>
                
                {/* Desktop Vertical Menu */}
                <div className="hidden md:flex flex-col space-y-2">
                  {playlists.map(playlist => (
                    <button
                      key={playlist.id}
                      onClick={() => setActivePlaylistId(playlist.playlistId)}
                      className={`text-left px-4 py-3 rounded-lg font-medium transition-colors ${activePlaylistId === playlist.playlistId ? 'bg-amber-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-300'}`}
                    >
                      {playlist.title}
                    </button>
                  ))}
                </div>

                {/* Mobile Horizontal Pill Menu */}
                <div className="md:hidden flex overflow-x-auto gap-2 pb-4 scrollbar-hide">
                  {playlists.map(playlist => (
                    <button
                      key={playlist.id}
                      onClick={() => setActivePlaylistId(playlist.playlistId)}
                      className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${activePlaylistId === playlist.playlistId ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                    >
                      {playlist.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Pane - Video Grid */}
            <div className="w-full md:w-3/4">
              {activePlaylist ? (
                <div key={activePlaylist.playlistId} className="animate-in fade-in duration-300">
                  <h2 className="text-2xl font-bold text-white mb-6 border-b border-slate-800 pb-2">
                    {activePlaylist.title}
                  </h2>
                  {activeVideos.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {activeVideos.map((video, idx) => (
                        <div
                          key={video.youtubeId || idx}
                          className={`group ${video.youtubeId ? 'cursor-pointer' : 'cursor-default'}`}
                          onClick={() => video.youtubeId && setSelectedVideo(video.youtubeId)}
                        >
                          <div className="aspect-video bg-slate-800 rounded-lg relative overflow-hidden ring-1 ring-slate-700 group-hover:ring-amber-500 transition-all flex items-center justify-center">
                            {video.youtubeId ? (
                              <>
                                <img
                                  src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
                                  alt={video.title}
                                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                                  <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                                    <Play className="h-8 w-8 text-white ml-1" fill="white" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 opacity-80" />
                                <div className="text-center z-10">
                                  <PlayCircle className="w-12 h-12 text-slate-500 group-hover:text-amber-500 transition-colors mx-auto mb-2 opacity-50 group-hover:opacity-100" />
                                  <span className="text-slate-400 text-sm font-medium">Coming Soon</span>
                                </div>
                              </>
                            )}
                          </div>
                          <h4 className="text-white font-medium mt-3 group-hover:text-amber-500 transition-colors">{video.title}</h4>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-800">
                      <PlayCircle className="w-12 h-12 text-slate-600 mx-auto mb-3 opacity-50" />
                      <p className="text-slate-400 text-sm">No videos found in this playlist, or API key is not configured.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-800">
                  <p className="text-slate-400">No playlists available.</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      <Footer />

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4">
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-4 right-4 text-white hover:text-amber-500 z-10"
          >
            <X className="h-8 w-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-xl overflow-hidden bg-black/50 shadow-2xl">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyPage;
